/**
 * Email AI-assist hooks — summarize, draft, categorize. These bill (the server
 * routes go through invokeEntity with the 20% AI markup).
 *
 * `useDraftReply` streams its result token-by-token over SSE (the same shape
 * the plugin bridge uses), so the draft appears live in the composer.
 */

import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { consumeSSE } from '@/lib/sse-parser';

export function useSummarizeMessage() {
  return useMutation({
    mutationFn: (messageId: string) => api.summarizeEmailMailboxMessage(messageId),
  });
}

export interface DraftStreamHandlers {
  /** Called with each token as it arrives. */
  onToken: (text: string) => void;
  /** Called once with the final (trimmed) draft when the stream completes. */
  onDone?: (output: string) => void;
}

export interface DraftReplyController {
  /** Start a streaming draft. Resolves when the stream ends (or errors). */
  start: (
    input: { instructions: string; replyToMessageId?: string },
    handlers: DraftStreamHandlers,
  ) => Promise<void>;
  /** Abort an in-flight stream. */
  cancel: () => void;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  /** Clear the error state (e.g. when the composer reopens). */
  reset: () => void;
}

export function useDraftReply(): DraftReplyController {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const start = useCallback(
    async (
      input: { instructions: string; replyToMessageId?: string },
      handlers: DraftStreamHandlers,
    ) => {
      setIsPending(true);
      setError(null);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const response = await api.streamEmailMailboxDraft(input, { signal: controller.signal });
        // A non-2xx response is a regular JSON error (e.g. body validation);
        // SSE error frames arrive on a 200 stream and are handled below.
        if (!response.ok) {
          let message = 'Draft request failed';
          try {
            const body = await response.json();
            message = body?.error?.message ?? message;
          } catch {
            /* non-JSON body — keep the default message */
          }
          throw new Error(message);
        }
        await new Promise<void>((resolve) => {
          consumeSSE(
            response,
            handlers.onToken,
            (output) => {
              handlers.onDone?.(output);
              resolve();
            },
            (message) => {
              setError(new Error(message));
              resolve();
            },
          );
        });
      } catch (err) {
        // Aborts are intentional (composer closed / restarted) — not errors.
        if (!(err instanceof Error && err.name === 'AbortError')) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        setIsPending(false);
        abortRef.current = null;
      }
    },
    [],
  );

  const cancel = useCallback(() => abortRef.current?.abort(), []);
  const reset = useCallback(() => setError(null), []);

  return { start, cancel, isPending, isError: error !== null, error, reset };
}

export function useCategorizeMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.categorizeEmailMailboxMessage(messageId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.email.mailbox.threads() }),
  });
}
